import {
  Box,
  Button,
  CurrencyIcon,
  EMPTY_ARRAY,
  FormControlSizes,
  HStack,
  Icon,
  IconName,
  isMultileg,
  type LegDirectionEnum,
  SideEnum,
  Tooltip,
  useCallbackRef,
  useSecuritiesContext,
} from '@talos/kyoko';
import { capitalize, noop, uniq } from 'lodash-es';
import { useMemo, useState } from 'react';
import styled from 'styled-components';
import { DefaultLegSummary } from './DefaultLegSummary';
import { Section } from './styles';
import type { MultiLegSummaryProps } from './types';
import { useUnifiedLiquidityMarketDetails } from './useUnifiedLiquidityMarkets';

const SummaryPanelWrapper = styled(Box)``;
SummaryPanelWrapper.defaultProps = {
  borderRadius: 'borderRadiusDefault',
  mb: 'spacing8',
};

function legSide(orderSide: SideEnum, legDirection: LegDirectionEnum) {
  const oppositeSide = orderSide === 'Buy' ? 'Sell' : 'Buy';
  return legDirection === 'Same' ? orderSide : oppositeSide;
}

export const DefaultMultilegSummary = ({
  security,
  // we default the order's side to be Buy for displaying multileg details if there is no order side specified
  // this can be useful in admin cases etc. In other cases such as orders / market data, this should always be specified
  side = SideEnum.Buy,
  readOnly,
  initiatingLegs,
  haircutRatios,
  legMarkets,
  legMarketSelectorItems,
  unifiedLiquidityToken,
  unifiedLiquidityMarkets,
  unifiedLiquidityMarketSelectorItems,
  supportsAllMarkets,
  touched,
  onEditSymbol,
  onUnifiedLiquidityMarketsChange = noop,
  onLegMarketsChange = noop,
  onInitiatingLegChange = noop,
  onHaircutRatioChange = noop,
  allocations,
  enableHaircutRatio,
  orderQuantity,
  orderCurrency,
}: MultiLegSummaryProps) => {
  const { securitiesBySymbol } = useSecuritiesContext();
  const [expandedLeg, setExpandedLeg] = useState<number>(-1);

  const defaultLegMarkets = useMemo(
    () => security.MultilegDetails?.Legs?.map(leg => leg.Markets?.map(m => m.MarketAccount) ?? []) ?? [[], []],
    [security]
  );
  const actualLegMarkets = legMarkets ?? defaultLegMarkets;

  const defaultInitiatingLegs = useMemo(
    () => security.MultilegDetails?.Parameters?.LegParams?.map(legParam => legParam.Initiating) ?? [],
    [security]
  );
  const actualInitiatingLegs = initiatingLegs ?? defaultInitiatingLegs;

  const defaultHaircutRatios = useMemo(
    () => security.MultilegDetails?.Parameters?.LegParams?.map(legParam => legParam.HaircutRatio) ?? [],
    [security]
  );
  const actualHaircutRatios = haircutRatios ?? defaultHaircutRatios;

  const hasInitiatingError = !actualInitiatingLegs.some(t => t);
  const hasMarketError =
    !supportsAllMarkets &&
    (actualLegMarkets.some(m => m.length === 0) ||
      (unifiedLiquidityToken != null && unifiedLiquidityMarkets?.length === 0));
  const legDetails = security.MultilegDetails?.Legs ?? [];

  const availableULMarkets = useUnifiedLiquidityMarketDetails(unifiedLiquidityToken);
  const ulSymbols = uniq(unifiedLiquidityToken?.Tokens?.map(t => t.Symbol) ?? []);
  const ulDisplaySymbols = ulSymbols.map(symbol => securitiesBySymbol.get(symbol)?.DisplaySymbol ?? symbol).join(', ');

  const handleUnifiedLiquidityMarketsChange = useCallbackRef(({ value }: { legIndex: number; value: string[] }) => {
    onUnifiedLiquidityMarketsChange(value);
  });

  if (!isMultileg(security)) {
    return null;
  }
  if (!security.MultilegDetails?.Legs?.length) {
    return null;
  }

  const errors = capitalize(
    [
      hasInitiatingError ? 'At least 1 leg must Initiate' : undefined,
      hasMarketError ? 'Markets are required for all legs' : undefined,
    ]
      .compact()
      .join(' and ')
  );

  return (
    <SummaryPanelWrapper data-testid="multileg-summary-panel">
      <Section isExpanded={false} nonExpandedRows={1}>
        <HStack flex={1} justifyContent="space-between" gap="spacing4" my="spacing4">
          <HStack gap="spacing4" color="colorTextImportant" overflow="hidden">
            <CurrencyIcon currency={security.BaseCurrency} size={12} stacked={!!unifiedLiquidityToken} />
            <Box lineHeight={1} whiteSpace="nowrap" fontSize="fontSizeSmall" overflow="hidden" textOverflow="ellipsis">
              {security?.DisplaySymbol}
              {unifiedLiquidityToken ? '+' : ''}
            </Box>
          </HStack>
          {hasInitiatingError || hasMarketError ? (
            <Tooltip tooltip={errors}>
              <Icon
                icon={IconName.Exclamation}
                data-testid="multileg-summary-initiating-error"
                color="colorTextNegative"
              />
            </Tooltip>
          ) : null}
          {onEditSymbol && !readOnly && (
            <Box m="-spacing2">
              <Tooltip tooltip="Edit multileg symbol">
                <Button
                  data-testid="edit-multileg-symbol"
                  size={FormControlSizes.Tiny}
                  startIcon={IconName.Pencil}
                  onClick={() => onEditSymbol(security.Symbol)}
                >
                  Edit
                </Button>
              </Tooltip>
            </Box>
          )}
        </HStack>
      </Section>

      {legDetails.map((leg, index) => {
        return (
          <DefaultLegSummary
            /** Whenever the symbol we are showing changes, we remount the component. There is code which needs to re-init when the symbol changes.
             * One example is the code to compute the available markets.
             */
            key={`${security.Symbol}-${leg.Symbol}-${index}`}
            legIndex={index}
            symbol={leg.Symbol}
            isExpanded={expandedLeg === index}
            onToggle={() => setExpandedLeg(index === expandedLeg ? -1 : index)}
            isInitiating={actualInitiatingLegs[index]}
            haircutRatio={actualHaircutRatios[index]}
            side={legSide(side, leg.Direction)}
            prettyLegName={leg.Symbol}
            overrideMarkets={actualLegMarkets[index]}
            markets={leg.Markets ?? undefined}
            marketSelectorItems={legMarketSelectorItems?.at(index)}
            readOnly={readOnly || !!onEditSymbol}
            showLegMarketChanges={false}
            touched={touched}
            supportsAllMarkets={supportsAllMarkets}
            onLegMarketsChange={onLegMarketsChange}
            onInitiatingLegChange={onInitiatingLegChange}
            onHaircutRatioChange={onHaircutRatioChange}
            enableHaircutRatio={enableHaircutRatio}
            subAccountAllocations={allocations}
            orderQuantity={orderQuantity}
            orderCurrency={orderCurrency}
          />
        );
      })}
      {unifiedLiquidityToken ? (
        <DefaultLegSummary
          /** Whenever the symbol we are showing changes, we remount the component. There is code which needs to re-init when the symbol changes.
           * One example is the code to compute the available markets.
           */
          key={`${security.Symbol}-${unifiedLiquidityToken.Symbol}`}
          legIndex={undefined}
          symbol={ulSymbols[0]}
          isExpanded={expandedLeg === 2}
          onToggle={() => setExpandedLeg(2 === expandedLeg ? -1 : 2)}
          isInitiating={actualInitiatingLegs[2]}
          haircutRatio={actualHaircutRatios[2]}
          side={side} // the order's side is used for the UL leg
          prettyLegName={ulDisplaySymbols}
          overrideMarkets={unifiedLiquidityMarkets ?? EMPTY_ARRAY}
          marketSelectorItems={unifiedLiquidityMarketSelectorItems}
          markets={availableULMarkets}
          readOnly={readOnly}
          touched={touched}
          showLegMarketChanges={false}
          supportsAllMarkets={supportsAllMarkets}
          onLegMarketsChange={handleUnifiedLiquidityMarketsChange}
          onInitiatingLegChange={onInitiatingLegChange}
          onHaircutRatioChange={onHaircutRatioChange}
          enableHaircutRatio={enableHaircutRatio}
          subAccountAllocations={allocations}
          orderQuantity={orderQuantity}
          orderCurrency={orderCurrency}
        />
      ) : null}
    </SummaryPanelWrapper>
  );
};
